import { useState, useEffect } from 'react';

const LOCAL_STORAGE_KEY = 'evite_dividas_local_expenses';
const LOCAL_STORAGE_META_KEY = 'evite_dividas_local_meta';
const LOCAL_STORAGE_INCOME_KEY = 'evite_dividas_local_income';
const LOCAL_STORAGE_RECUR_MARKERS_KEY = 'evite_dividas_recur_markers';

// Shift a 'YYYY-MM' month string by delta months
const shiftMonth = (month, delta) => {
  const y = parseInt(month.slice(0, 4), 10);
  const m = parseInt(month.slice(5, 7), 10);
  const idx = (y * 12 + (m - 1)) + delta;
  const ny = Math.floor(idx / 12);
  const nm = (idx % 12) + 1;
  return `${String(ny).padStart(4, '0')}-${String(nm).padStart(2, '0')}`;
};

// Advance a 'YYYY-MM-DD' date by N months, clamping the day to the last valid day
const advanceDate = (dateStr, months) => {
  const y = parseInt(dateStr.slice(0, 4), 10);
  const m = parseInt(dateStr.slice(5, 7), 10);
  const d = parseInt(dateStr.slice(8, 10), 10);
  const idx = (y * 12 + (m - 1)) + months;
  const ny = Math.floor(idx / 12);
  const nm = (idx % 12) + 1;
  const lastDay = new Date(ny, nm, 0).getDate();
  const nd = Math.min(d, lastDay);
  return `${String(ny).padStart(4, '0')}-${String(nm).padStart(2, '0')}-${String(nd).padStart(2, '0')}`;
};

const randId = () => `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Categorias padrão disponíveis para usuários anônimos
export const DEFAULT_CATEGORIES = [
  { id: 'moradia', category_id: 'moradia', name: 'Moradia', icon: 'home', color: '#3B82F6', is_default: true },
  { id: 'alimentacao', category_id: 'alimentacao', name: 'Alimentação', icon: 'utensils', color: '#F59E0B', is_default: true },
  { id: 'transporte', category_id: 'transporte', name: 'Transporte', icon: 'car', color: '#8B5CF6', is_default: true },
  { id: 'lazer', category_id: 'lazer', name: 'Lazer', icon: 'gamepad', color: '#EC4899', is_default: true },
  { id: 'saude', category_id: 'saude', name: 'Saúde', icon: 'heart', color: '#EF4444', is_default: true },
  { id: 'educacao', category_id: 'educacao', name: 'Educação', icon: 'book', color: '#10B981', is_default: true },
  { id: 'outros', category_id: 'outros', name: 'Outros', icon: 'more-horizontal', color: '#6B7280', is_default: true },
];

export const useLocalExpenses = () => {
  const [localExpenses, setLocalExpenses] = useState([]);
  const [localIncome, setLocalIncome] = useState([]);
  const [localMeta, setLocalMeta] = useState(null);

  // Carregar dados do localStorage na inicialização
  useEffect(() => {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    const storedMeta = localStorage.getItem(LOCAL_STORAGE_META_KEY);
    const storedIncome = localStorage.getItem(LOCAL_STORAGE_INCOME_KEY);
    
    if (stored) {
      try {
        setLocalExpenses(JSON.parse(stored));
      } catch (e) {
        console.error('Error parsing local expenses:', e);
        setLocalExpenses([]);
      }
    }
    
    if (storedIncome) {
      try {
        setLocalIncome(JSON.parse(storedIncome));
      } catch (e) {
        console.error('Error parsing local income:', e);
        setLocalIncome([]);
      }
    }
    
    if (storedMeta) {
      try {
        setLocalMeta(JSON.parse(storedMeta));
      } catch (e) {
        console.error('Error parsing local meta:', e);
      }
    } else {
      // Primeira visita - salvar timestamp
      const meta = {
        firstVisit: new Date().toISOString(),
        lastVisit: new Date().toISOString()
      };
      localStorage.setItem(LOCAL_STORAGE_META_KEY, JSON.stringify(meta));
      setLocalMeta(meta);
    }
  }, []);

  // Atualizar localStorage quando as despesas mudam
  useEffect(() => {
    if (localExpenses.length > 0) {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(localExpenses));
    }
  }, [localExpenses]);

  // Atualizar localStorage quando as rendas mudam
  useEffect(() => {
    if (localIncome.length > 0) {
      localStorage.setItem(LOCAL_STORAGE_INCOME_KEY, JSON.stringify(localIncome));
    }
  }, [localIncome]);

  // Atualizar última visita
  const updateLastVisit = () => {
    if (localMeta) {
      const updated = { ...localMeta, lastVisit: new Date().toISOString() };
      localStorage.setItem(LOCAL_STORAGE_META_KEY, JSON.stringify(updated));
      setLocalMeta(updated);
    }
  };

  // Adicionar despesa localmente
  const addLocalExpense = (expense) => {
    const isParcelada = !!(expense.installments_total && expense.installments_total > 1);
    const isRecurring = !!expense.is_fixed || isParcelada;
    const newExpense = {
      ...expense,
      expense_id: `local_${randId()}`,
      recurrence_id: isRecurring ? `rec_${randId()}` : null,
      installments_total: isParcelada ? expense.installments_total : null,
      installment_number: isParcelada ? 1 : null,
      created_at: new Date().toISOString()
    };
    setLocalExpenses(prev => [...prev, newExpense]);
    return newExpense;
  };

  // Atualizar despesa
  const updateLocalExpense = (expenseId, updates) => {
    setLocalExpenses(prev => 
      prev.map(exp => exp.expense_id === expenseId ? { ...exp, ...updates } : exp)
    );
  };

  // Remover despesa
  const deleteLocalExpense = (expenseId) => {
    setLocalExpenses(prev => prev.filter(exp => exp.expense_id !== expenseId));
  };

  // Obter despesas por mês
  const getExpensesByMonth = (month) => {
    return localExpenses.filter(exp => exp.date?.startsWith(month));
  };

  // Calcular resumo mensal
  const getSummary = (month) => {
    const monthExpenses = getExpensesByMonth(month);
    const total = monthExpenses.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);
    const totalPaid = monthExpenses.filter(e => e.is_paid).reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);
    const totalUnpaid = total - totalPaid;
    
    // Income for the month
    const monthIncome = localIncome.filter(inc => inc.date?.startsWith(month));
    const totalIncome = monthIncome.reduce((sum, inc) => sum + (parseFloat(inc.amount) || 0), 0);
    const saldo = totalIncome - totalPaid;
    
    // Agrupar por categoria
    const byCategory = {};
    monthExpenses.forEach(exp => {
      if (!byCategory[exp.category_id]) {
        const cat = DEFAULT_CATEGORIES.find(c => c.id === exp.category_id) || { name: 'Outros', color: '#6B7280', icon: 'more-horizontal' };
        byCategory[exp.category_id] = {
          category_id: exp.category_id,
          name: cat.name,
          color: cat.color,
          icon: cat.icon,
          total: 0
        };
      }
      byCategory[exp.category_id].total += parseFloat(exp.amount) || 0;
    });

    const categories = Object.values(byCategory).map(cat => ({
      ...cat,
      percentage: total > 0 ? Math.round((cat.total / total) * 100) : 0
    })).sort((a, b) => b.total - a.total);

    return {
      total,
      total_income: totalIncome,
      total_paid: totalPaid,
      total_unpaid: totalUnpaid,
      saldo,
      categories,
      expense_count: monthExpenses.length
    };
  };

  // === Income (Renda) functions ===
  
  const addLocalIncome = (income) => {
    const isFixed = income.income_type === 'fixa';
    const newIncome = {
      ...income,
      income_id: `local_inc_${randId()}`,
      recurrence_id: isFixed ? `rec_${randId()}` : null,
      created_at: new Date().toISOString()
    };
    setLocalIncome(prev => [...prev, newIncome]);
    return newIncome;
  };

  const updateLocalIncome = (incomeId, updates) => {
    setLocalIncome(prev =>
      prev.map(inc => inc.income_id === incomeId ? { ...inc, ...updates } : inc)
    );
  };

  const deleteLocalIncome = (incomeId) => {
    setLocalIncome(prev => prev.filter(inc => inc.income_id !== incomeId));
  };

  const getIncomeByMonth = (month) => {
    return localIncome.filter(inc => inc.date?.startsWith(month));
  };

  // Gera automaticamente itens recorrentes (fixos / parcelados) copiando mês a mês
  // do mês anterior, até o mês alvo. Roda uma vez por (mês, tipo) via marcadores.
  // Copiar apenas do mês imediatamente anterior faz com que apagar um item recorrente
  // pare a recorrência para sempre ("parar de vez").
  const ensureRecurringLocal = (targetMonth) => {
    if (!targetMonth) return;
    let markers = {};
    try { markers = JSON.parse(localStorage.getItem(LOCAL_STORAGE_RECUR_MARKERS_KEY) || '{}'); } catch (e) { markers = {}; }

    const expenses = [...localExpenses];
    const incomes = [...localIncome];

    const allMonths = [...expenses.map(e => e.date), ...incomes.map(i => i.date)]
      .filter(Boolean).map(d => d.slice(0, 7)).sort();
    if (allMonths.length === 0) return;
    const earliest = allMonths[0];

    let changed = false;
    let markersChanged = false;
    let m = shiftMonth(earliest, 1);
    let guard = 0;
    while (m <= targetMonth && guard < 240) {
      guard++;
      ['expense', 'income'].forEach(kind => {
        const key = `${m}_${kind}`;
        if (markers[key]) return;
        markers[key] = true;
        markersChanged = true;
        const prev = shiftMonth(m, -1);
        if (kind === 'expense') {
          const prevItems = expenses.filter(x => x.date?.startsWith(prev) && (x.is_fixed || x.installments_total));
          const existing = new Set(expenses.filter(x => x.date?.startsWith(m) && x.recurrence_id).map(x => x.recurrence_id));
          prevItems.forEach(it => {
            if (it.recurrence_id && existing.has(it.recurrence_id)) return;
            let newNum = null;
            if (it.installments_total) {
              newNum = (it.installment_number || 1) + 1;
              if (newNum > it.installments_total) return; // parcelas acabaram
            }
            expenses.push({
              ...it,
              expense_id: `local_${randId()}`,
              date: advanceDate(it.date, 1),
              is_paid: false,
              installment_number: newNum,
              created_at: new Date().toISOString()
            });
            changed = true;
          });
        } else {
          const prevItems = incomes.filter(x => x.date?.startsWith(prev) && x.income_type === 'fixa');
          const existing = new Set(incomes.filter(x => x.date?.startsWith(m) && x.recurrence_id).map(x => x.recurrence_id));
          prevItems.forEach(it => {
            if (it.recurrence_id && existing.has(it.recurrence_id)) return;
            incomes.push({
              ...it,
              income_id: `local_inc_${randId()}`,
              date: advanceDate(it.date, 1),
              created_at: new Date().toISOString()
            });
            changed = true;
          });
        }
      });
      m = shiftMonth(m, 1);
    }

    if (markersChanged) {
      localStorage.setItem(LOCAL_STORAGE_RECUR_MARKERS_KEY, JSON.stringify(markers));
    }
    if (changed) {
      setLocalExpenses(expenses);
      setLocalIncome(incomes);
    }
  };

  // Verificar se deve mostrar popup de login (após 24h)
  const shouldShowLoginPrompt = () => {
    if (!localMeta?.firstVisit || localExpenses.length === 0) return false;
    
    const firstVisit = new Date(localMeta.firstVisit);
    const now = new Date();
    const hoursSinceFirst = (now - firstVisit) / (1000 * 60 * 60);
    
    // Mostrar se passaram mais de 24h E tem despesas
    return hoursSinceFirst >= 24;
  };

  // Verificar se é um retorno (voltou depois de algum tempo)
  const isReturningUser = () => {
    if (!localMeta?.lastVisit) return false;
    
    const lastVisit = new Date(localMeta.lastVisit);
    const now = new Date();
    const hoursSinceLast = (now - lastVisit) / (1000 * 60 * 60);
    
    // Considera retorno se passou mais de 1 hora da última visita
    return hoursSinceLast >= 1;
  };

  // Limpar dados locais (após migração para conta)
  const clearLocalData = () => {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    localStorage.removeItem(LOCAL_STORAGE_META_KEY);
    localStorage.removeItem(LOCAL_STORAGE_INCOME_KEY);
    localStorage.removeItem(LOCAL_STORAGE_RECUR_MARKERS_KEY);
    setLocalExpenses([]);
    setLocalIncome([]);
    setLocalMeta(null);
  };

  // Obter dados para migração
  const getDataForMigration = () => {
    return {
      expenses: localExpenses,
      income: localIncome,
      meta: localMeta
    };
  };

  // Verificar se tem dados locais
  const hasLocalData = () => {
    return localExpenses.length > 0;
  };

  return {
    localExpenses,
    localIncome,
    localMeta,
    categories: DEFAULT_CATEGORIES,
    addLocalExpense,
    updateLocalExpense,
    deleteLocalExpense,
    getExpensesByMonth,
    getSummary,
    addLocalIncome,
    updateLocalIncome,
    deleteLocalIncome,
    getIncomeByMonth,
    ensureRecurringLocal,
    shouldShowLoginPrompt,
    isReturningUser,
    clearLocalData,
    getDataForMigration,
    hasLocalData,
    updateLastVisit
  };
};

export default useLocalExpenses;
